import React, { useState, useEffect, useCallback, useRef } from 'react';
import { adminAPI, authAPI, companyAPI } from '../services/api';
import { PageLayout, DataTable, Badge, Modal, ConfirmDialog, StatusBadge, SearchFilterBar, Avatar } from './ui';
import { startImpersonation } from './ImpersonationBanner';
import { Mail, Trash2, UserCog, Ban, UserX, Power, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';
import { isUserActive, getUserStatus } from '../utils/userHelpers';
import { useAuth } from '../context/AuthContext';

const inputClass = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary focus:border-transparent';
const btnPrimary = 'px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary-hover transition-colors disabled:opacity-50';
const btnSecondary = 'px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors';

const AdminSecurityKeys = ({ userId }) => {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    authAPI.adminGetWebAuthnKeys(userId)
      .then(data => setKeys(data.keys || []))
      .catch(() => setKeys([]))
      .finally(() => setLoading(false));
  }, [userId]);

  const handleRevoke = async (keyId, keyName) => {
    if (!window.confirm(`Revoke security key "${keyName}" for this user?`)) return;
    try {
      await authAPI.adminRevokeWebAuthnKey(userId, keyId);
      setKeys(prev => prev.filter(k => k.id !== keyId));
    } catch { /* silent */ }
  };

  if (loading) return null;
  if (keys.length === 0) return null;

  return (
    <div>
      <label className="block text-xs text-gray-500 mb-1">Security Keys ({keys.length})</label>
      <div className="space-y-1">
        {keys.map(key => (
          <div key={key.id} className="flex items-center justify-between p-2 border border-gray-200 rounded-lg text-xs">
            <div>
              <span className="font-medium text-gray-900">{key.device_name}</span>
              <span className="text-gray-400 ml-2">
                Added {new Date(key.created_at).toLocaleDateString()}
              </span>
            </div>
            <button
              onClick={() => handleRevoke(key.id, key.device_name)}
              className="text-red-600 hover:text-red-900 text-xs"
            >
              Revoke
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

const AdminPanel = () => {
  const { user: authUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [auditEvents, setAuditEvents] = useState([]);
  const [activeTab, setActiveTab] = useState('users');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newUser, setNewUser] = useState({
    email: '', password: '', firstname: '', lastname: '',
    permission_level: 'user', send_welcome_email: true,
  });
  const [creating, setCreating] = useState(false);
  const [sendingEmailFor, setSendingEmailFor] = useState(null);

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null, confirmLabel: 'Confirm', variant: 'danger', loading: false });

  // Slide-over panel state
  const [panelUser, setPanelUser] = useState(null);
  const [panelTab, setPanelTab] = useState('Profile');
  const [panelCompanies, setPanelCompanies] = useState([]);
  const [panelLoading, setPanelLoading] = useState(false);
  const [panelError, setPanelError] = useState('');
  const [panelSuccess, setPanelSuccess] = useState('');
  const [panelPermissionPreview, setPanelPermissionPreview] = useState(null);
  const [panelSessions, setPanelSessions] = useState([]);
  const [panelRevokingSession, setPanelRevokingSession] = useState(null);

  // Roles state (for companies tab in panel)
  const [availableRoles, setAvailableRoles] = useState([]);
  const [userCompanyRoles, setUserCompanyRoles] = useState({});
  const [expandedCompany, setExpandedCompany] = useState(null);
  const [companyAllowedRoleIds, setCompanyAllowedRoleIds] = useState({});
  const [companySearch, setCompanySearch] = useState('');
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [allCompanies, setAllCompanies] = useState([]);

  // Search, filter, pagination state
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterPermission, setFilterPermission] = useState('');
  const [filterCompany, setFilterCompany] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const perPage = 50;
  const usersRef = useRef([]);

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const usersData = await adminAPI.getUsers({
        search: debouncedSearch,
        permission_level: filterPermission,
        company_id: filterCompany,
        page: currentPage,
        per_page: perPage,
      });
      if (usersData.status === 'success') {
        setUsers(usersData.users);
        usersRef.current = usersData.users;
        setTotalUsers(usersData.total || 0);
        if (usersData.pending_count !== undefined) setPendingCount(usersData.pending_count);
      } else {
        setError(usersData.error || 'Failed to load users');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, filterPermission, filterCompany, currentPage]);

  useEffect(() => {
    if (activeTab === 'users') {
      loadUsers();
    }
  }, [activeTab, loadUsers]);

  // Debounce search input — only update debouncedSearch after 300ms of inactivity
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const formatDate = (dateString) => new Date(dateString).toLocaleString();

  const loadAudit = async () => {
    try {
      const auditData = await adminAPI.getAuditEvents();
      if (auditData.status === 'success' && Array.isArray(auditData.events)) {
        setAuditEvents(auditData.events);
      }
    } catch (err) {
      console.error('Failed to load audit events:', err);
    }
  };

  const loadCompanies = async () => {
    try {
      const companiesData = await companyAPI.listCompanies();
      setAllCompanies(Array.isArray(companiesData) ? companiesData : (companiesData.companies || []));
    } catch (err) {
      console.error('Failed to load companies:', err);
    }
  };

  // Load audit events and companies on mount (separate from paginated user loading)
  useEffect(() => {
    loadAudit();
    loadCompanies();
  }, []);

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setCreating(true);
    setError('');
    setSuccess('');
    try {
      const response = await adminAPI.createUser(newUser);
      if (response.status === 'success') {
        setSuccess('User created successfully');
        setIsCreateModalOpen(false);
        setNewUser({ email: '', password: '', firstname: '', lastname: '', permission_level: 'user', send_welcome_email: true });
        loadUsers();
        loadAudit();
      } else {
        setError(response.error || 'Failed to create user');
      }
    } catch (error) {
      setError(error.error || error.message || 'Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      setConfirmDialog(prev => ({ ...prev, loading: true }));
      const response = await adminAPI.deleteUser(userId);
      if (response.status === 'success') {
        setUsers(users.filter(u => u.id !== userId));
        usersRef.current = usersRef.current.filter(u => u.id !== userId);
        setSuccess('User deleted');
        setPanelUser(null);
        loadAudit();
      } else {
        setError(response.error || 'Failed to delete user');
      }
    } catch (error) {
      setError(error.error || error.message || 'Failed to delete user');
    } finally {
      setConfirmDialog({ open: false, title: '', message: '', onConfirm: null, confirmLabel: 'Confirm', variant: 'danger', loading: false });
    }
  };

  const handleSendWelcomeEmail = async (userId) => {
    setSendingEmailFor(userId);
    setPanelError('');
    setPanelSuccess('');
    setConfirmDialog(prev => ({ ...prev, loading: true }));
    try {
      const response = await adminAPI.sendWelcomeEmail(userId);
      if (response.status === 'success') {
        setPanelSuccess('Welcome email sent');
      } else {
        setPanelError(response.error || 'Failed to send welcome email');
      }
    } catch (error) {
      setPanelError(error.error || error.message || 'Failed to send welcome email');
    } finally {
      setSendingEmailFor(null);
      setConfirmDialog({ open: false, title: '', message: '', onConfirm: null, confirmLabel: 'Confirm', variant: 'danger', loading: false });
    }
  };

  const handleImpersonate = async (user) => {
    setError('');
    try {
      await startImpersonation(user.id);
      window.location.href = '/';
    } catch (err) {
      setError(err.error || err.message || 'Failed to start impersonation');
    }
  };

  // --- Slide-over panel handlers ---

  const openUserPanel = async (user) => {
    setPanelUser(user);
    setPanelTab('Profile');
    setPanelError('');
    setPanelSuccess('');
    setPanelLoading(true);
    setPanelSessions([]);
    setPanelPermissionPreview(null);
    setUserCompanyRoles({});
    setCompanyAllowedRoleIds({});
    setExpandedCompany(null);
    setCompanySearch('');
    setShowCompanyDropdown(false);
    try {
      setPanelCompanies(user.companies || []);
      // Load roles
      const rolesData = await adminAPI.getAdminRoles();
      if (rolesData.status === 'success') setAvailableRoles(rolesData.roles || []);
      // Load user-company roles
      const ucRolesData = await adminAPI.getUserCompanyRoles(user.id);
      if (ucRolesData.status === 'success') {
        const rolesByCompany = {};
        (ucRolesData.roles || []).forEach(r => {
          if (!rolesByCompany[r.company_id]) rolesByCompany[r.company_id] = [];
          rolesByCompany[r.company_id].push(r.role_id);
        });
        setUserCompanyRoles(rolesByCompany);
      }
      // Load permission preview
      try {
        const previewData = await adminAPI.getPermissionPreview(user.id);
        setPanelPermissionPreview(previewData);
      } catch {
        setPanelPermissionPreview(null);
      }
    } catch (err) {
      setPanelError('Failed to load user details');
    } finally {
      setPanelLoading(false);
    }
  };

  const closePanel = () => {
    setPanelUser(null);
    setPanelCompanies([]);
    setPanelPermissionPreview(null);
    setPanelSessions([]);
  };

  const handleUpdateCompanyRoles = async (companyId, roleIds) => {
    try {
      const data = await adminAPI.updateUserCompanyRoles(panelUser.id, companyId, roleIds);
      if (data.status === 'success') {
        setUserCompanyRoles(prev => ({ ...prev, [companyId]: roleIds }));
        setPanelSuccess('Roles updated');
      } else {
        setPanelError(data.error || 'Failed to update roles');
      }
    } catch (error) {
      setPanelError(error.error || error.message || 'Failed to update roles');
    }
  };

  const toggleRole = (companyId, roleId) => {
    const allowed = companyAllowedRoleIds[companyId];
    if (allowed instanceof Set && !allowed.has(roleId)) return;
    const currentRoles = userCompanyRoles[companyId] || [];
    const newRoles = currentRoles.includes(roleId)
      ? currentRoles.filter(id => id !== roleId)
      : [...currentRoles, roleId];
    handleUpdateCompanyRoles(companyId, newRoles);
  };

  const handleAddUserCompany = async (companyId) => {
    try {
      const data = await adminAPI.addUserCompany(panelUser.id, companyId);
      if (data.status === 'success') {
        const uc = data.user_company;
        const normalized = {
          company_id: uc.company_id,
          company: { id: uc.company_id, name: uc.company_name || uc.company?.name || 'Unknown' },
          is_primary: uc.is_primary || false,
          roles: [],
        };
        setPanelCompanies([...panelCompanies, normalized]);
        setCompanySearch('');
        setShowCompanyDropdown(false);
        setPanelSuccess('Company added');
        // Refresh user list to show updated company
        loadUsers();
      } else {
        setPanelError(data.error || 'Failed to add company');
      }
    } catch (error) {
      setPanelError(error.error || error.message || 'Failed to add company');
    }
  };

  const handleRemoveUserCompany = async (companyId) => {
    try {
      const data = await adminAPI.removeUserCompany(panelUser.id, companyId);
      if (data.status === 'success') {
        setPanelCompanies(panelCompanies.filter(uc => (uc.company_id || uc.company?.id) !== companyId));
        setPanelSuccess('Company removed');
        // Refresh user list to show updated company
        loadUsers();
      } else {
        setPanelError(data.error || 'Failed to remove company');
      }
    } catch (error) {
      setPanelError(error.error || error.message || 'Failed to remove company');
    }
  };

  const handleSetPrimaryCompany = async (companyId) => {
    try {
      const data = await adminAPI.setPrimaryCompany(panelUser.id, companyId);
      if (data.status === 'success') {
        setPanelCompanies(panelCompanies.map(uc => ({ ...uc, is_primary: (uc.company_id || uc.company?.id) === companyId })));
        setPanelSuccess('Primary company updated');
        // Refresh user list to show updated company
        loadUsers();
      } else {
        setPanelError(data.error || 'Failed to set primary company');
      }
    } catch (error) {
      setPanelError(error.error || error.message || 'Failed to set primary company');
    }
  };

  const filteredCompanies = allCompanies.filter(c =>
    !panelCompanies.some(uc => (uc.company_id || uc.company?.id) === c.id) &&
    c.name.toLowerCase().includes(companySearch.toLowerCase())
  ).slice(0, 10);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const userColumns = [
    {
      key: 'user',
      label: 'User',
      sortable: true,
      sortValue: (u) => `${u.firstname} ${u.lastname}`.toLowerCase(),
      render: (user) => {
        return (
          <div className="flex items-center">
            <Avatar user={user} size="md" />
            <div className="ml-4">
              <div className="text-sm font-medium text-gray-900">{user.firstname} {user.lastname}</div>
              <div className="text-sm text-gray-500">{user.email}</div>
            </div>
          </div>
        );
      },
    },
    {
      key: 'company',
      label: 'Company',
      render: (user) => <span className="text-sm text-gray-500">{user.company ? user.company.name : '—'}</span>,
    },
    {
      key: 'permission_level',
      label: 'Global Admin',
      sortable: true,
      render: (user) => (
        <Badge color={user.permission_level === 'global_admin' ? 'blue' : 'gray'}>
          {user.permission_level === 'global_admin' ? 'Yes' : 'No'}
        </Badge>
      ),
    },
    {
      key: 'lifecycle_state',
      label: 'Status',
      sortable: true,
      render: (user) => <StatusBadge status={getUserStatus(user)} />,
    },
    {
      key: 'created_at',
      label: 'Created',
      sortable: true,
      render: (user) => <span className="text-sm text-gray-500">{formatDate(user.created_at)}</span>,
    },
  ];

  const auditColumns = [
    {
      key: 'event_type',
      label: 'Event Type',
      render: (event) => (
        <Badge color={event.event_type.includes('SUCCESS') ? 'green' : event.event_type.includes('FAIL') ? 'red' : 'primary'}>
          {event.event_type}
        </Badge>
      ),
    },
    { key: 'description', label: 'Description', render: (event) => <span className="text-sm text-gray-500">{event.description}</span> },
    { key: 'ip_address', label: 'IP Address', render: (event) => <span className="text-sm text-gray-500">{event.ip_address}</span> },
    { key: 'event_time', label: 'Time', sortable: true, render: (event) => <span className="text-sm text-gray-500">{formatDate(event.event_time)}</span> },
  ];

  const isImpersonating = !!authUser?.impersonating;
  const panelTopOffset = isImpersonating ? '104px' : '64px';

  return (
    <PageLayout
      title="User Management"
      subtitle="Global view: All users across all companies (use Team Members for company-scoped operations)"
      backTo="/management"
      backLabel="Back to Management"
      actions={activeTab === 'users' ? (
        <button onClick={() => setIsCreateModalOpen(true)} className={btnPrimary}>Create User</button>
      ) : null}
    >
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button onClick={() => setActiveTab('users')}
            className={`${activeTab === 'users' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2`}>
            Users
            {pendingCount > 0 && (
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-500 text-white text-xs font-bold">{pendingCount}</span>
            )}
          </button>
          <button onClick={() => setActiveTab('audit')}
            className={`${activeTab === 'audit' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}>
            Audit Log
          </button>
        </nav>
      </div>

      {success && <div className="bg-green-50 border-l-4 border-green-400 p-3 mb-4 text-sm text-green-700">{success}</div>}
      {error && <div className="bg-red-50 border-l-4 border-red-400 p-3 mb-4 text-sm text-red-700">{error}</div>}

      {activeTab === 'users' && (
        <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 mb-4 text-sm text-blue-700">
          <strong>How to use:</strong> Search or filter to find a user, then click their row to open the detail panel.
          From there you can edit profile, assign companies and roles, change lifecycle state (suspend/deactivate/offboard),
          view active sessions, send invite email, impersonate, or delete.
        </div>
      )}

      {activeTab === 'users' && pendingCount > 0 && filterCompany !== 'pending' && (
        <button
          onClick={() => setFilterCompany('pending')}
          className="w-full bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-sm text-amber-800 text-left hover:bg-amber-100 transition-colors flex items-center gap-2"
        >
          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-amber-500 text-white text-xs font-bold">{pendingCount}</span>
          <span><strong>{pendingCount} user{pendingCount !== 1 ? 's' : ''}</strong> pending company assignment. Click to view.</span>
        </button>
      )}

      {activeTab === 'users' ? (
        <>
          <SearchFilterBar
            searchValue={searchTerm}
            onSearchChange={setSearchTerm}
            searchPlaceholder="Search by name or email..."
            filters={[
              {
                value: filterPermission,
                onChange: setFilterPermission,
                options: [
                  { value: '', label: 'All types' },
                  { value: 'global_admin', label: 'Global Admins' },
                  { value: 'user', label: 'Standard Users' },
                ],
              },
              {
                value: filterCompany,
                onChange: setFilterCompany,
                options: [
                  { value: '', label: 'All companies' },
                  { value: 'pending', label: `Pending approval${pendingCount > 0 ? ` (${pendingCount})` : ''}` },
                  ...allCompanies.map(c => ({ value: String(c.id), label: c.name })),
                ],
              },
            ]}
          />
          <DataTable columns={userColumns} data={users} emptyMessage="No users found" onRowClick={openUserPanel} />
          {totalUsers > perPage && (
            <div className="flex items-center justify-between mt-4">
              <span className="text-sm text-gray-500">
                Showing {(currentPage - 1) * perPage + 1}–{Math.min(currentPage * perPage, totalUsers)} of {totalUsers}
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="p-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-sm font-medium text-gray-700">
                  Page {currentPage} of {Math.ceil(totalUsers / perPage)}
                </span>
                <button
                  onClick={() => setCurrentPage(p => Math.min(Math.ceil(totalUsers / perPage), p + 1))}
                  disabled={currentPage >= Math.ceil(totalUsers / perPage)}
                  className="p-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          <DataTable columns={auditColumns} data={auditEvents} emptyMessage="No audit events found" />
          <div className="mt-3 flex gap-2">
            <button onClick={() => adminAPI.exportAuditEvents('csv')} className={btnSecondary}>Export CSV</button>
            <button onClick={() => adminAPI.exportAuditEvents('json')} className={btnSecondary}>Export JSON</button>
          </div>
        </>
      )}

      {/* Create User Modal */}
      <Modal isOpen={isCreateModalOpen} onClose={() => { setIsCreateModalOpen(false); setError(''); }} title="Create New User" size="md">
        <form onSubmit={handleCreateUser}>
          {error && (
            <div className="mb-4 bg-red-50 border-l-4 border-red-400 p-3 text-sm text-red-700">
              {error}
            </div>
          )}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input type="email" required className={inputClass} value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Password <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <input type="password" className={inputClass} value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                placeholder="Leave blank to let user set their own" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                <input type="text" required className={inputClass} value={newUser.firstname}
                  onChange={(e) => setNewUser({ ...newUser, firstname: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                <input type="text" required className={inputClass} value={newUser.lastname}
                  onChange={(e) => setNewUser({ ...newUser, lastname: e.target.value })} />
              </div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={newUser.send_welcome_email}
                onChange={(e) => setNewUser({ ...newUser, send_welcome_email: e.target.checked })}
                className="rounded border-gray-300 text-primary focus:ring-primary" />
              <span className="text-sm text-gray-700">Send welcome email with password setup link</span>
            </label>
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <button type="button" onClick={() => { setIsCreateModalOpen(false); setError(''); }} className={btnSecondary}>Cancel</button>
            <button type="submit" disabled={creating} className={btnPrimary}>
              {creating ? 'Creating...' : 'Create User'}
            </button>
          </div>
        </form>
      </Modal>

      {/* User Detail Slide-over Panel */}
      {panelUser && (
        <UserSlidePanel
          user={panelUser}
          tab={panelTab}
          setTab={setPanelTab}
          loading={panelLoading}
          error={panelError}
          setError={setPanelError}
          success={panelSuccess}
          companies={panelCompanies}
          onClose={closePanel}
          permissionPreview={panelPermissionPreview}
          sessions={panelSessions}
          setSessions={setPanelSessions}
          revokingSession={panelRevokingSession}
          setRevokingSession={setPanelRevokingSession}
          topOffset={panelTopOffset}
          availableRoles={availableRoles}
          userCompanyRoles={userCompanyRoles}
          expandedCompany={expandedCompany}
          setExpandedCompany={setExpandedCompany}
          companyAllowedRoleIds={companyAllowedRoleIds}
          setCompanyAllowedRoleIds={setCompanyAllowedRoleIds}
          companySearch={companySearch}
          setCompanySearch={setCompanySearch}
          showCompanyDropdown={showCompanyDropdown}
          setShowCompanyDropdown={setShowCompanyDropdown}
          filteredCompanies={filteredCompanies}
          onAddCompany={handleAddUserCompany}
          onRemoveCompany={handleRemoveUserCompany}
          onSetPrimary={handleSetPrimaryCompany}
          onToggleRole={toggleRole}
          onUserUpdated={async (newState) => { 
            if (newState) {
              setPanelUser(prev => ({ ...prev, lifecycle_state: newState }));
            }
            await loadUsers(); 
            const fresh = usersRef.current.find(u => u.id === panelUser.id); 
            if (fresh) openUserPanel(fresh); 
          }}
          onDeleteUser={() => setConfirmDialog({
            open: true,
            title: 'Delete User',
            message: `Permanently delete ${panelUser.firstname} ${panelUser.lastname} (${panelUser.email})? This cannot be undone.`,
            confirmLabel: 'Delete',
            variant: 'danger',
            onConfirm: () => handleDeleteUser(panelUser.id),
          })}
          onImpersonate={() => setConfirmDialog({
            open: true,
            title: 'Impersonate User',
            message: `You will be acting as ${panelUser.firstname} ${panelUser.lastname} (${panelUser.email}). All actions are audited.`,
            confirmLabel: 'Impersonate',
            variant: 'primary',
            onConfirm: () => handleImpersonate(panelUser),
          })}
          onSendInvite={() => setConfirmDialog({
            open: true,
            title: 'Send Welcome Email',
            message: `Send welcome/password reset email to ${panelUser.firstname} ${panelUser.lastname} (${panelUser.email})?`,
            confirmLabel: 'Send Email',
            variant: 'primary',
            onConfirm: () => handleSendWelcomeEmail(panelUser.id),
          })}
          sendingInvite={sendingEmailFor === panelUser?.id}
        />
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, title: '', message: '', onConfirm: null, confirmLabel: 'Confirm', variant: 'danger', loading: false })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmLabel={confirmDialog.confirmLabel}
        variant={confirmDialog.variant}
        loading={confirmDialog.loading}
      />
    </PageLayout>
  );
};

// --- Slide-over panel component ---
const UserSlidePanel = ({
  user, tab, setTab, loading, error, setError, success,
  companies, onClose, permissionPreview,
  sessions, setSessions, revokingSession, setRevokingSession,
  topOffset,
  availableRoles, userCompanyRoles, expandedCompany, setExpandedCompany,
  companyAllowedRoleIds, setCompanyAllowedRoleIds,
  companySearch, setCompanySearch, showCompanyDropdown, setShowCompanyDropdown,
  filteredCompanies, onAddCompany, onRemoveCompany, onSetPrimary, onToggleRole,
  onUserUpdated, onDeleteUser, onImpersonate, onSendInvite, sendingInvite,
}) => {
  const [editForm, setEditForm] = useState({ firstname: '', lastname: '', permission_level: 'user' });
  const [saving, setSaving] = useState(false);
  const [lifecycleLoading, setLifecycleLoading] = useState(false);
  const [lifecycleMsg, setLifecycleMsg] = useState('');
  const [sessionsLoaded, setSessionsLoaded] = useState(false);

  useEffect(() => {
    if (user) {
      setEditForm({
        firstname: user.firstname || '',
        lastname: user.lastname || '',
        permission_level: user.permission_level || 'user',
      });
    }
  }, [user]);

  // Auto-load sessions when Sessions tab is selected
  useEffect(() => {
    if (tab === 'Sessions' && user && !sessionsLoaded) {
      loadSessions();
      setSessionsLoaded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, user, sessionsLoaded]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await adminAPI.updateUser(user.id, editForm);
      if (onUserUpdated) onUserUpdated();
    } catch (err) {
      setError(err.error || err.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleLifecycleChange = async (newState) => {
    setLifecycleLoading(true);
    setLifecycleMsg('');
    try {
      const result = await adminAPI.setUserLifecycle(user.id, newState);
      setLifecycleMsg(result.message || `State changed to ${newState}`);
      // Notify parent to refresh
      if (onUserUpdated) onUserUpdated(newState);
    } catch (err) {
      setLifecycleMsg(err.error || err.message || 'Failed to update lifecycle state');
    } finally {
      setLifecycleLoading(false);
    }
  };

  const handleTestSSO = async (appId) => {
    try {
      await startImpersonation(user.id);
      window.open(`/api/saml/sso/${appId}`, '_blank');
    } catch (err) {
      setLifecycleMsg(err.error || err.message || 'Failed to start impersonation for SSO test');
    }
  };

  const loadSessions = async () => {
    try {
      const data = await companyAPI.adminListUserSessions(user.id);
      setSessions(data.sessions || []);
    } catch (err) {
      setError(err.error || err.message || 'Failed to load sessions');
    }
  };

  const handleRevokeSession = async (sessionId) => {
    setRevokingSession(sessionId);
    try {
      const data = await companyAPI.adminRevokeUserSession(user.id, sessionId);
      if (data.status === 'success') {
        setSessions(prev => prev.filter(x => x.id !== sessionId));
      } else {
        setError(data.error || data.message || 'Failed to revoke session');
      }
    } catch (err) {
      console.error('Failed to revoke session:', err);
      setError(err.error || err.message || 'Failed to revoke session');
    } finally {
      setRevokingSession(null);
    }
  };

  const tabs = ['Profile', 'Companies', 'Sessions'];

  return (
    <div className="fixed right-0 bottom-0 bg-white border-l border-gray-200 shadow-2xl z-40 overflow-y-auto" style={{ top: topOffset, width: '900px', maxWidth: '90vw' }}>
      {/* Header */}
      <div className="sticky top-0 bg-white border-b border-gray-200 px-5 py-4 flex items-center gap-3 z-10">
        <Avatar user={user} size="lg" />
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-gray-900 truncate">{user.firstname} {user.lastname}</div>
          <div className="text-sm text-gray-500 truncate">{user.email}</div>
        </div>
        <StatusBadge status={getUserStatus(user)} />
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none ml-2">×</button>
      </div>

      {/* Tabs */}
      <div className="sticky top-[73px] bg-white border-b border-gray-200 flex z-10">
        {tabs.map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
              tab === t ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}>
            {t}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="px-6 pt-7 pb-10">
        {loading && (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        )}

        {!loading && error && (
          <div className="bg-red-50 border-l-4 border-red-400 p-3 text-sm text-red-700 mb-4">{error}</div>
        )}

        {!loading && success && (
          <div className="bg-green-50 border-l-4 border-green-400 p-3 text-sm text-green-700 mb-4">{success}</div>
        )}

        {/* Profile tab */}
        {!loading && tab === 'Profile' && (
          <div className="space-y-5">
            <div>
              <label className="block text-xs text-gray-500 mb-1">First name</label>
              <input type="text" value={editForm.firstname}
                onChange={(e) => setEditForm({ ...editForm, firstname: e.target.value })}
                className={inputClass} />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Last name</label>
              <input type="text" value={editForm.lastname}
                onChange={(e) => setEditForm({ ...editForm, lastname: e.target.value })}
                className={inputClass} />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Email <span className="text-gray-400 normal-case">(identifier — cannot be changed)</span></label>
              <input type="text" value={user.email || ''} readOnly
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-gray-50 text-gray-500 cursor-not-allowed" />
            </div>

            {/* Lifecycle state */}
            <div>
              <label className="block text-xs text-gray-500 mb-1">Lifecycle state</label>
              <div className="flex items-center gap-2">
                <span className={`px-2 py-1 text-xs font-medium rounded ${
                  getUserStatus(user) === 'active' ? 'bg-green-100 text-green-700' :
                  getUserStatus(user) === 'suspended' ? 'bg-yellow-100 text-yellow-700' :
                  getUserStatus(user) === 'offboarded' ? 'bg-gray-200 text-gray-600' :
                  'bg-red-100 text-red-700'
                }`}>
                  {getUserStatus(user)}
                </span>
              </div>
              {lifecycleMsg && (
                <div className={`mt-2 text-xs ${lifecycleMsg.includes('Failed') || lifecycleMsg.includes('Cannot') ? 'text-red-600' : 'text-green-600'}`}>
                  {lifecycleMsg}
                </div>
              )}
              <div className="flex flex-wrap gap-2 mt-2">
                {!isUserActive(user) && (
                  <button onClick={() => handleLifecycleChange('active')} disabled={lifecycleLoading}
                    className="flex items-center gap-1 px-2 py-1 text-xs bg-green-50 text-green-700 border border-green-200 rounded hover:bg-green-100 disabled:opacity-50">
                    <RefreshCw className="w-3 h-3" /> Reactivate
                  </button>
                )}
                {isUserActive(user) && (
                  <button onClick={() => handleLifecycleChange('suspended')} disabled={lifecycleLoading}
                    className="flex items-center gap-1 px-2 py-1 text-xs bg-yellow-50 text-yellow-700 border border-yellow-200 rounded hover:bg-yellow-100 disabled:opacity-50">
                    <Ban className="w-3 h-3" /> Suspend
                  </button>
                )}
                {(isUserActive(user) || getUserStatus(user) === 'suspended') && (
                  <button onClick={() => handleLifecycleChange('deactivated')} disabled={lifecycleLoading}
                    className="flex items-center gap-1 px-2 py-1 text-xs bg-orange-50 text-orange-700 border border-orange-200 rounded hover:bg-orange-100 disabled:opacity-50">
                    <UserX className="w-3 h-3" /> Deactivate
                  </button>
                )}
                {(isUserActive(user) || getUserStatus(user) === 'suspended' || getUserStatus(user) === 'deactivated') && (
                  <button onClick={() => handleLifecycleChange('offboarded')} disabled={lifecycleLoading}
                    className="flex items-center gap-1 px-2 py-1 text-xs bg-red-50 text-red-700 border border-red-200 rounded hover:bg-red-100 disabled:opacity-50">
                    <Power className="w-3 h-3" /> Offboard
                  </button>
                )}
              </div>
            </div>

            {/* MFA — hidden for SSO users (Decision 15: MFA handled by Microsoft) */}
            {user.auth_method !== 'sso' && (!user.oauth_providers || user.oauth_providers.length === 0) && (
            <div>
              <label className="block text-xs text-gray-500 mb-1">Multi-factor authentication</label>
              <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                  user.mfa_enabled ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                }`}>
                  {user.mfa_enabled ? 'Configured' : 'Not configured'}
                </span>
              </div>
            </div>
            )}

            {/* Security Keys */}
            <AdminSecurityKeys userId={user.id} />

            {/* Permission Preview */}
            <div className="mt-2">
              <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Permission Preview</div>
              <p className="text-xs text-gray-400 mb-3">What this user will see when they log in</p>
              {permissionPreview && permissionPreview.applications && permissionPreview.applications.length > 0 ? (
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {permissionPreview.applications.map(app => (
                    <div key={app.id} className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-gray-900">{app.name}</span>
                        <button onClick={() => handleTestSSO(app.id)} disabled={!isUserActive(user)}
                          className="text-xs text-primary hover:text-primary-hover disabled:opacity-40 font-medium">
                          Test SSO →
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-1 mb-2">
                        {app.roles.map(r => (
                          <span key={r.role_id} className="px-1.5 py-0.5 text-xs bg-primary-light text-primary rounded">{r.role_name}</span>
                        ))}
                      </div>
                      {app.settings.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {app.settings.map(s => (
                            <div key={s.key} className="flex items-center justify-between text-xs">
                              <span className="text-gray-600 font-medium">{s.key}</span>
                              <span className="text-gray-900">{s.value || '—'}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <p className="text-xs text-gray-400">No applications assigned — assign a role with applications to see permissions here</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Companies tab */}
        {!loading && tab === 'Companies' && (
          <div className="space-y-3">
            {companies.length > 0 ? (
              companies.map((uc, idx) => (
                <div key={idx} className="bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center justify-between px-3 py-2">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <button type="button"
                        onClick={async () => {
                          const newId = expandedCompany === (uc.company_id || uc.company?.id) ? null : (uc.company_id || uc.company?.id);
                          setExpandedCompany(newId);
                          if (newId && companyAllowedRoleIds[newId] === undefined) {
                            try {
                              const data = await companyAPI.getAllowedRoles(newId);
                              // Internal companies: store null to indicate all roles allowed
                              if (data.is_internal) {
                                setCompanyAllowedRoleIds(prev => ({ ...prev, [newId]: null }));
                              } else {
                                const ids = new Set((data.allowed_roles || []).map(r => r.role_id));
                                setCompanyAllowedRoleIds(prev => ({ ...prev, [newId]: ids }));
                              }
                            } catch {
                              setCompanyAllowedRoleIds(prev => ({ ...prev, [newId]: new Set() }));
                            }
                          }
                        }}
                        className="text-gray-400 hover:text-gray-600 flex-shrink-0">
                        <svg className={`w-4 h-4 transform transition-transform ${expandedCompany === (uc.company_id || uc.company?.id) ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                      <span className="text-sm text-gray-900 truncate">{uc.company?.name || 'Unknown'}</span>
                      {uc.is_primary && <span className="text-xs bg-primary-light text-primary px-2 py-0.5 rounded flex-shrink-0">Primary</span>}
                      {(userCompanyRoles[uc.company_id || uc.company?.id] || []).length > 0 && (
                        <span className="text-xs text-gray-500 truncate">
                          ({availableRoles.filter(r => (userCompanyRoles[uc.company_id || uc.company?.id] || []).includes(r.id)).map(r => r.name).join(', ')})
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {!uc.is_primary && (
                        <button onClick={() => onSetPrimary(uc.company_id || uc.company?.id)}
                          className="text-xs text-primary hover:text-primary-hover px-2 py-1">Set Primary</button>
                      )}
                      <button onClick={() => onRemoveCompany(uc.company_id || uc.company?.id)}
                        className="text-red-500 hover:text-red-700 p-1" title="Remove company">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  {expandedCompany === (uc.company_id || uc.company?.id) && (
                    <div className="px-3 pb-2 pt-1 border-t border-gray-200 bg-white rounded-b-lg">
                      {(() => {
                        const allowed = companyAllowedRoleIds[uc.company_id || uc.company?.id];
                        // null = internal company (all allowed), Set = explicit allowed list
                        const isInternal = allowed === null;
                        const noRolesConfigured = allowed instanceof Set && allowed.size === 0;
                        return noRolesConfigured ? (
                          <div className="text-xs text-red-600 bg-red-50 border border-red-100 p-2 rounded-lg mt-1 mb-2">
                            No roles allowed for this company. Add roles in Company Management first.
                          </div>
                        ) : (
                          <div className="text-xs text-gray-500 mb-1">Roles:{isInternal && <span className="text-green-600 ml-1">(internal — all roles allowed)</span>}</div>
                        );
                      })()}
                      <div className="flex flex-wrap gap-2">
                        {availableRoles.map(role => {
                          const allowed = companyAllowedRoleIds[uc.company_id || uc.company?.id];
                          // null = internal (all allowed), Set = check membership
                          const isDisallowed = allowed instanceof Set && !allowed.has(role.id);
                          return (
                            <label key={role.id} className={`flex items-center gap-1 text-sm ${isDisallowed ? 'cursor-not-allowed opacity-40' : 'cursor-pointer'}`}>
                              <input type="checkbox"
                                checked={(userCompanyRoles[uc.company_id || uc.company?.id] || []).includes(role.id)}
                                onChange={() => onToggleRole(uc.company_id || uc.company?.id, role.id)} disabled={isDisallowed}
                                className="rounded border-gray-300 text-primary focus:ring-primary" />
                              <span>{role.name}</span>
                            </label>
                          );
                        })}
                        {availableRoles.length === 0 && <span className="text-xs text-gray-400">No roles available</span>}
                      </div>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-500 text-center py-4">No companies assigned</p>
            )}

            {/* Add company dropdown */}
            <div className="relative pt-2">
              <input type="text" placeholder="Search and add company..." className={inputClass}
                value={companySearch}
                onChange={(e) => { setCompanySearch(e.target.value); setShowCompanyDropdown(true); }}
                onFocus={() => setShowCompanyDropdown(true)} />
              {showCompanyDropdown && companySearch && filteredCompanies.length > 0 && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {filteredCompanies.map(company => (
                    <button key={company.id} type="button"
                      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 focus:bg-gray-100"
                      onClick={() => onAddCompany(company.id)}>
                      {company.name}
                    </button>
                  ))}
                </div>
              )}
              {showCompanyDropdown && companySearch && filteredCompanies.length === 0 && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg p-3">
                  <p className="text-sm text-gray-500">No matching companies found</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Sessions tab */}
        {!loading && tab === 'Sessions' && (
          <div className="space-y-3">
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-sm font-medium text-gray-900">Active Sessions</h3>
              <button onClick={() => { setSessionsLoaded(false); }} className="text-xs text-primary hover:text-primary-hover">Refresh</button>
            </div>
            {sessions.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-sm text-gray-500">No active sessions</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {sessions.map(s => (
                  <div key={s.id} className="p-3 border border-gray-200 rounded-lg">
                    <div className="flex items-start justify-between">
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-gray-900">{s.ip_address || 'Unknown IP'}</div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          {[s.city, s.country].filter(Boolean).join(', ') || 'Unknown location'}
                        </div>
                        <div className="text-xs text-gray-400 mt-1 truncate" title={s.user_agent}>{s.user_agent || 'Unknown browser'}</div>
                        <div className="text-xs text-gray-400 mt-1">
                          Created: {s.created_at ? new Date(s.created_at).toLocaleString() : '-'}
                        </div>
                        {s.expires_at && (
                          <div className="text-xs text-gray-400 mt-0.5">
                            Expires: {new Date(s.expires_at).toLocaleString()}
                          </div>
                        )}
                      </div>
                      <button onClick={() => handleRevokeSession(s.id)} disabled={revokingSession === s.id}
                        className="ml-2 text-xs text-red-600 hover:text-red-700 disabled:opacity-50 whitespace-nowrap">
                        {revokingSession === s.id ? 'Revoking...' : 'Revoke'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Action bar — only Save is always visible. Impersonate/Delete only on Profile tab */}
      <div className="sticky bottom-0 bg-white border-t border-gray-200 px-5 py-3 space-y-2">
        {tab === 'Profile' && (
          <div className="flex gap-2">
            <button onClick={onSendInvite} disabled={sendingInvite}
              className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-primary bg-primary-light border border-primary rounded-lg hover:bg-primary hover:text-white disabled:opacity-50">
              <Mail className="w-4 h-4" />
              {sendingInvite ? 'Sending...' : 'Send Invite'}
            </button>
            <button onClick={onImpersonate} disabled={!isUserActive(user)}
              className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50">
              <UserCog className="w-4 h-4" />
              Impersonate
            </button>
            <button onClick={onDeleteUser}
              className="flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100">
              <Trash2 className="w-4 h-4" />
              Delete
            </button>
          </div>
        )}
        {tab === 'Profile' && (
          <button onClick={handleSave} disabled={saving}
            className="w-full py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-hover disabled:opacity-50">
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;
